import { uid } from 'quasar';
import { careModelAnalytics } from 'src/services/analytics';

import { countCohortCriteria } from '../../domain/services/cohortService';
import { useCohortsFiltersColumns } from './useCohortsFiltersColumns';

/** @typedef {
 *   import('src/services/analytics/analyticsTypes').CohortCriterionEventParams
 * } CohortCriterionEventParams */
/** @typedef {
 *   import('../../domain/entities/CareModelCohort.types').CareModelCohort
 * } CareModelCohort */
/** @typedef {
 *   import('../../domain/entities/CareModelCohort.types').CohortFilters
 * } CohortFilters */
/** @typedef {
 *   import('../../domain/entities/CareModelCohort.types').CohortFilterStatement
 * } CohortFilterStatement */

/**
 * Analytics for the cohorts settings page. Every event of one open-to-close pass over the cohort
 * dialog shares a cohortEditAttemptId, so flows abandoned before the first save (which have no cohort ID yet),
 * can still be tied to their start.
 */
// eslint-disable-next-line import/prefer-default-export
export function useCohortsAnalytics() {
  const { columns } = useCohortsFiltersColumns();

  /**
   * @param {Object} params
   * @param {string} params.cohortEditAttemptId
   * @param {string|null} params.cohortId
   * @param {string} params.filterKey - Key of the filter the criterion belongs to
   * @param {CohortFilterStatement} params.statement - Criterion as picked in the filter UI
   * @param {CohortFilters} params.filters - Cohort filters after the change
   * @param {boolean} params.isReplacement - Whether the criterion replaced an existing one
   * @returns {CohortCriterionEventParams}
   */
  function getCriterionEventParams({
    cohortEditAttemptId,
    cohortId = null,
    filterKey,
    statement,
    filters,
    isReplacement = false,
  }) {
    return {
      cohortEditAttemptId,
      cohortId: cohortId || null,
      isNewCohort: !cohortId,
      isReplacement,
      criteriaCount: countCohortCriteria(filters),
      criterion: {
        ...careModelAnalytics.describeCriterionField(filterKey, columns.value),
        statement,
      },
    };
  }

  /**
   * @param {Object} params
   * @param {CareModelCohort} params.cohort - Cohort being opened, empty when a new one is created
   * @param {CareModelCohort[]} params.cohorts - Cohorts as ordered on the page
   * @returns {string} - Attempt id to carry through the rest of this dialog's events
   */
  function trackCohortDialogOpened({ cohort, cohorts = [] }) {
    const cohortEditAttemptId = uid();

    if (!cohort?.id) {
      careModelAnalytics.trackCohortCreationStarted({
        cohortEditAttemptId,
        totalCohorts: cohorts.length,
      });

      return cohortEditAttemptId;
    }

    const cohortIndex = cohorts.findIndex((item) => item.id === cohort.id);

    careModelAnalytics.trackCohortEditOpened({
      cohortEditAttemptId,
      cohortId: cohort.id,
      criteriaCount: countCohortCriteria(cohort.filters),
      cohortPosition: cohortIndex >= 0 ? cohortIndex + 1 : null,
      totalCohorts: cohorts.length,
    });

    return cohortEditAttemptId;
  }

  /**
   * @param {CohortCriterionEventParams} params
   * @returns {void}
   */
  function trackCohortCriterionAdded(params) {
    careModelAnalytics.trackCohortCriterionAdded(getCriterionEventParams(params));
  }

  /**
   * @param {CohortCriterionEventParams} params
   * @returns {void}
   */
  function trackCohortCriterionRemoved(params) {
    careModelAnalytics.trackCohortCriterionRemoved(getCriterionEventParams(params));
  }

  /**
   * @param {Object} params
   * @param {string} params.cohortEditAttemptId
   * @param {string|null} params.cohortId
   * @param {boolean} params.isCreate
   * @param {CohortFilters} params.filters - Cohort filters as saved
   * @returns {void}
   */
  function trackCohortSaved({ cohortEditAttemptId, cohortId = null, isCreate = false, filters }) {
    careModelAnalytics.trackCohortSaved({
      cohortEditAttemptId,
      cohortId: cohortId || null,
      isCreate,
      criteriaCount: countCohortCriteria(filters),
    });
  }

  /**
   * @param {Object} params
   * @param {string} params.cohortEditAttemptId
   * @param {string} params.cohortId
   * @param {CohortFilters} params.filters - Cohort filters the deleted cohort had
   * @param {number} params.unassignedClientsCount - Clients the deletion unassigned from the cohort
   * @returns {void}
   */
  function trackCohortDeleted({ cohortEditAttemptId, cohortId, filters, unassignedClientsCount = 0 }) {
    careModelAnalytics.trackCohortDeleted({
      cohortEditAttemptId,
      cohortId,
      criteriaCount: countCohortCriteria(filters),
      unassignedClientsCount,
    });
  }

  return {
    trackCohortDialogOpened,
    trackCohortCriterionAdded,
    trackCohortCriterionRemoved,
    trackCohortSaved,
    trackCohortDeleted,
  };
}
